import { useEffect, useRef, useState } from 'react';
import type React from 'react';
import { Box, Flex, Text, VStack, chakra, useBreakpointValue } from '@chakra-ui/react';
import { PinOff, X } from 'lucide-react';
import { formatChatMessagePreview } from '@/components/chat/formatChatMessagePreview';
import type { ChatPin } from '@/hooks/useChatPins';

/** Drag distance past which releasing dismisses the sheet. Matches NewDirectChatModal. */
const DISMISS_DRAG_PX = 120;

type Props = {
  open: boolean;
  onClose: () => void;
  pins: ChatPin[];
  canPin: boolean;
  onUnpin: (messageId: string) => void;
  /** Jump to the message in the thread. Closing the sheet is the caller's job. */
  onJumpTo: (pin: ChatPin) => void;
};

/**
 * Every pinned message on this thread, with an unpin action for admins.
 *
 * A bottom sheet on phones and a centered dialog from `md`, copying
 * `NewDirectChatModal` in this same folder — same drag-to-dismiss, same safe-area
 * handling, same backdrop that lightens as the sheet is pulled away. Deliberately not
 * `BaseModal`: that is a centered dialog only, and a list you reach from a banner at the
 * top of a full-screen chat should come up from the bottom on a phone like every other
 * sheet on this page.
 */
export function PinnedMessagesSheet({
  open,
  onClose,
  pins,
  canPin,
  onUnpin,
  onJumpTo,
}: Props) {
  const [entered, setEntered] = useState(false);
  const [dragY, setDragY] = useState(0);
  const [dragging, setDragging] = useState(false);
  const dragStartYRef = useRef(0);
  const dragCapturedRef = useRef(false);

  const canDrag = useBreakpointValue({ base: true, md: false }) ?? true;

  useEffect(() => {
    if (!open) {
      setEntered(false);
      setDragY(0);
      setDragging(false);
      return;
    }
    // Start off-screen, then animate in on the next frame — setting both in the same
    // frame would paint the final position with no transition.
    const raf = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(raf);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Bound to the handle/header strip only, so it can never fight the list below it for
  // the same vertical gesture.
  const onDragStart = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!canDrag) return;
    if ((e.target as HTMLElement).closest('button, input')) return;
    dragStartYRef.current = e.clientY;
    dragCapturedRef.current = false;
    setDragging(true);
  };

  const onDragMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging) return;
    const dy = Math.max(0, e.clientY - dragStartYRef.current);
    if (!dragCapturedRef.current && dy > 4) {
      e.currentTarget.setPointerCapture(e.pointerId);
      dragCapturedRef.current = true;
    }
    setDragY(dy);
  };

  const onDragEnd = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging) return;
    setDragging(false);
    if (dragCapturedRef.current && e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    dragCapturedRef.current = false;
    if (dragY > DISMISS_DRAG_PX) {
      onClose();
      return;
    }
    setDragY(0);
  };

  if (!open) return null;

  return (
    <Box
      position="fixed"
      inset={0}
      bg="blackAlpha.600"
      zIndex={1400}
      display="flex"
      alignItems={{ base: 'flex-end', md: 'center' }}
      justifyContent="center"
      opacity={entered ? Math.max(0, 1 - dragY / 400) : 0}
      transition={dragging ? 'none' : 'opacity 0.25s ease-out'}
      onClick={onClose}
    >
      <Box
        role="dialog"
        aria-label="Pinned messages"
        bg="white"
        w={{ base: '100%', md: '440px' }}
        maxH={{ base: '80vh', md: '70vh' }}
        borderTopRadius="2xl"
        borderRadius={{ md: '2xl' }}
        display="flex"
        flexDirection="column"
        overflow="hidden"
        // Fixed element, so it must consume its own bottom safe-area inset.
        pb={{ base: 'env(safe-area-inset-bottom, 0px)', md: 0 }}
        transform={{
          base: entered ? `translateY(${dragY}px)` : 'translateY(100%)',
          md: 'none',
        }}
        transition={dragging ? 'none' : 'transform 0.3s ease-in-out'}
        boxShadow="0 -8px 30px rgba(0,0,0,0.18)"
        onClick={(e) => e.stopPropagation()}
      >
        <Box
          flexShrink={0}
          css={{ touchAction: canDrag ? 'none' : 'auto' }}
          cursor={{ base: 'grab', md: 'default' }}
          _active={{ cursor: { base: 'grabbing', md: 'default' } }}
          onPointerDown={onDragStart}
          onPointerMove={onDragMove}
          onPointerUp={onDragEnd}
          onPointerCancel={onDragEnd}
        >
          <Flex justify="center" align="center" h="28px" display={{ base: 'flex', md: 'none' }}>
            <Box
              w="36px"
              h="5px"
              borderRadius="full"
              bg={dragging ? 'volyx.darkGray' : 'volyx.mediumGray'}
              transition="background 0.15s"
            />
          </Flex>
          <Flex align="center" gap={2} p={4} borderBottomWidth={1} borderColor="gray.200">
            <Text fontSize="md" fontWeight="semibold" flex={1}>
              {pins.length === 1 ? 'Pinned message' : `Pinned messages (${pins.length})`}
            </Text>
            <chakra.button
              type="button"
              aria-label="Close"
              onClick={onClose}
              p={1}
              borderRadius="md"
              color="volyx.textSecondary"
              cursor="pointer"
              _hover={{ bg: 'volyx.lightGray' }}
            >
              <X size={18} />
            </chakra.button>
          </Flex>
        </Box>

        <Box flex={1} overflowY="auto" px={4} py={2}>
          {pins.length === 0 ? (
            <Text fontSize="sm" color="volyx.textSecondary" py={6} textAlign="center">
              Nothing is pinned in this chat.
            </Text>
          ) : (
            <VStack align="stretch" gap={0}>
              {pins.map((pin) => (
                <Flex
                  key={pin.id}
                  align="center"
                  gap={3}
                  py={3}
                  borderBottomWidth={1}
                  borderColor="gray.100"
                  _last={{ borderBottomWidth: 0 }}
                >
                  {/* A pin whose message has gone still lists, so an admin has something
                      to unpin -- but there is nothing to jump to, so it is not a button. */}
                  <chakra.button
                    type="button"
                    disabled={!pin.message}
                    onClick={() => {
                      if (pin.message) onJumpTo(pin);
                    }}
                    display="flex"
                    flexDirection="column"
                    alignItems="flex-start"
                    textAlign="start"
                    minW={0}
                    flex={1}
                    py={1}
                    borderRadius="md"
                    cursor={pin.message ? 'pointer' : 'default'}
                    _hover={pin.message ? { bg: 'volyx.lightGray' } : undefined}
                  >
                    <Text fontSize="sm" color="volyx.primary" lineClamp={2}>
                      {pin.message
                        ? formatChatMessagePreview(pin.message)
                        : 'This message is no longer available'}
                    </Text>
                    <Text fontSize="xs" color="volyx.textSecondary" mt={0.5}>
                      {new Date(pin.created_at).toLocaleString(undefined, {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </Text>
                  </chakra.button>
                  {canPin ? (
                    <chakra.button
                      type="button"
                      aria-label="Unpin message"
                      onClick={() => onUnpin(pin.message_id)}
                      flexShrink={0}
                      display="flex"
                      alignItems="center"
                      gap={1.5}
                      px={2.5}
                      py={1.5}
                      fontSize="xs"
                      borderRadius="md"
                      color="volyx.error"
                      cursor="pointer"
                      _hover={{ bg: 'volyx.lightGray' }}
                    >
                      <PinOff size={14} />
                      Unpin
                    </chakra.button>
                  ) : null}
                </Flex>
              ))}
            </VStack>
          )}
        </Box>
      </Box>
    </Box>
  );
}
